import type { Building } from "building-types/building/buildingTypes";
import type { DocEntityHandle, LevelHandle, RoomHandle } from "building-types/handles/docEntityHandleTypes";
import type { RoomOutlineProblem } from "building-types/rooms/checkRoomOutline";
import type { PolyLine2 } from "geom/polyLine2Types";

/**
 * During building generation, we may encounter errors that prevent us from generating a building.
 * These errors are thrown from {@link generateBuilding} and halt building generation.
 */
export type BuildingGenerationError =
  | InvalidRoomOutlineError
  | OverlappingRoomsError
  | HoleOnLevelError
  | MissingExpressionOptionSetError
  | ExpressionSyntaxError
  | InvalidPlanarGraphLoopsError
  | OptionStateInfiniteLoopError;

export enum BuildingGenerationErrorType {
  HOLE_ON_LEVEL = "HOLE_ON_LEVEL",
  INVALID_ROOM_OUTLINE = "INVALID_ROOM_OUTLINE",
  INVALID_PLANAR_GRAPH_LOOPS = "INVALID_PLANAR_GRAPH_LOOPS",
  OVERLAPPING_ROOMS_ERROR = "OVERLAPPING_ROOMS_ERROR",
  EXPRESSION_SYNTAX_ERROR = "EXPRESSION_SYNTAX_ERROR",
  MISSING_EXPRESSION_OPTION_SET_ERROR = "MISSING_EXPRESSION_OPTION_SET_ERROR",
  OPTION_STATE_INFINITE_LOOP = "OPTION_STATE_INFINITE_LOOP",
}

/**
 * Unlike many types in Higharc, this is not an interface. It is an abstract class that is extended
 * by each error type. Without the base error type, we don't get a stack trace.
 */
export abstract class BuildingGenerationErrorBase extends Error {
  /**
   * Each error also includes a partial {@link Building} that reflects the state of generation when
   * the error took place. This can be used to display context to the user about what went wrong.
   */
  partialBuilding: { doc: Building["doc"] } & Partial<Building>;

  constructor(
    public handle?: DocEntityHandle,
    message: string = "",
    readonly geometry: PolyLine2[] = [],
  ) {
    super(message);
  }
}

/**
 * This error is thrown when a room outline is invalid. This can happen when a room outline is self-intersecting,
 * not the correct orientation.
 */
export class InvalidRoomOutlineError extends BuildingGenerationErrorBase {
  type: BuildingGenerationErrorType.INVALID_ROOM_OUTLINE = BuildingGenerationErrorType.INVALID_ROOM_OUTLINE;
  constructor(
    readonly roomHandle: RoomHandle,
    readonly problem: RoomOutlineProblem,
  ) {
    super(roomHandle);
  }
}

/**
 * In Higharc, a user authors rooms as a polygon outline. These loops are then converted into a planar graph and they
 * must not overlap in order to ensure a 1:1 correspondence between planar graph faces and rooms.
 */
export class OverlappingRoomsError extends BuildingGenerationErrorBase {
  type: BuildingGenerationErrorType.OVERLAPPING_ROOMS_ERROR = BuildingGenerationErrorType.OVERLAPPING_ROOMS_ERROR;
  constructor(
    readonly levelHandle: LevelHandle,
    readonly roomHandles: RoomHandle[],
  ) {
    super(levelHandle);
  }
}

/**
 * In Higharc today, we don't support holes on levels. This error is thrown when a level has a hole in it. This might
 * happen because a building has courtyard. We should fix this as it really doesn't have to be this way.
 */
export class HoleOnLevelError extends BuildingGenerationErrorBase {
  type: BuildingGenerationErrorType.HOLE_ON_LEVEL = BuildingGenerationErrorType.HOLE_ON_LEVEL;
  constructor(
    readonly levelHandle: LevelHandle,
    levelName: string,
    geometry: PolyLine2[] = [],
  ) {
    super(levelHandle, `A room hole was detected on level "${levelName}".`, geometry);
  }
}

/**
 * An error that is created when a formula references an option set that doesn't exist.
 */
export class MissingExpressionOptionSetError extends BuildingGenerationErrorBase {
  type: BuildingGenerationErrorType.MISSING_EXPRESSION_OPTION_SET_ERROR =
    BuildingGenerationErrorType.MISSING_EXPRESSION_OPTION_SET_ERROR;
  constructor(
    public variableName: string,
    public formula: string,
  ) {
    super(undefined, "Missing option set " + variableName + " in formula " + formula);
  }
}

/**
 * An error that is created when a formula has a syntax error. This should be rare as most
 * formulas are created using {@link ConditionsBuilder}.
 */
export class ExpressionSyntaxError extends BuildingGenerationErrorBase {
  type: BuildingGenerationErrorType.EXPRESSION_SYNTAX_ERROR = BuildingGenerationErrorType.EXPRESSION_SYNTAX_ERROR;
  constructor(
    public formula: string,
    public contextString: string,
    parserMessage: string,
  ) {
    super(undefined, parserMessage);
  }
}

export class InvalidPlanarGraphLoopsError extends BuildingGenerationErrorBase {
  type: BuildingGenerationErrorType.INVALID_PLANAR_GRAPH_LOOPS = BuildingGenerationErrorType.INVALID_PLANAR_GRAPH_LOOPS;
  constructor(
    readonly levelHandle: LevelHandle,
    readonly geometry: PolyLine2[],
  ) {
    super(levelHandle, "Invalid planar graph loops", geometry);
  }
}

/**
 * This error is thrown when conditions on an option set cause infinite building generation loop.
 */
export class OptionStateInfiniteLoopError extends BuildingGenerationErrorBase {
  type: BuildingGenerationErrorType.OPTION_STATE_INFINITE_LOOP = BuildingGenerationErrorType.OPTION_STATE_INFINITE_LOOP;
  constructor() {
    super(undefined, `Invalid conditions evaluating option choices causing infinite loop`);
  }
}
